import { BreedMaintenanceType } from '@application/types';
import type { DogBreedWhereInput } from '@gql/graphql';
import type { BreedListPageFormValues } from '@presentation/pages';
import { uniq } from 'lodash/fp';
import { useCallback } from 'react';
import { nonAllergicBreedsNames } from '../../QuizResultsPageController/api/useBreedsWhereBuilder';

export const useBreedsWhereBuilder = () => {
  const breedsWhereBuilder = useCallback((form: BreedListPageFormValues): DogBreedWhereInput => {
    const whereInputs: DogBreedWhereInput[] = [];

    whereInputs.push({
      translations: {
        some: {
          AND: [
            { description: { not: { equals: '' } } },
            { description: { not: { equals: null } } },
          ],
        },
      },
    });

    whereInputs.push({
      isSubBreed: { equals: false },
    });

    if (form.allergic) {
      whereInputs.push({
        name: { in: nonAllergicBreedsNames },
      });
    }

    if (form.independant) {
      whereInputs.push({
        AND: [
          { independenceIndex: { in: [1, 2, 3, 4] } },
          { educationIndex: { in: [1, 2] } },
          { sociabilityIndex: { in: [2, 3] } },
        ],
      });
    } else if (form.independant === false) {
      whereInputs.push({
        AND: [
          { sociabilityIndex: { in: [2, 3] } },
          { educationIndex: { in: [1, 2] } },
          { foreignerSociabilityIndex: { in: [2, 3, 4, 5] } },
        ],
      });
    }

    if (form.size?.length) {
      whereInputs.push({
        sizeCategory: { in: form.size },
      });
    }

    if (form.dogType?.length) {
      whereInputs.push({
        dogType: { hasSome: form.dogType },
      });
    }

    if (form.maintenanceType?.length) {
      const careIndexesPerMaintenanceType = {
        [BreedMaintenanceType.LITTLE]: [1],
        [BreedMaintenanceType.AVERAGE]: [1, 2],
        [BreedMaintenanceType.MUCH]: [1, 2, 3],
      };
      const careIndexes = uniq(
        form.maintenanceType.reduce<number[]>((arr: any, type) => {
          return [...arr, ...careIndexesPerMaintenanceType[type]];
        }, []),
      );

      whereInputs.push({
        careIndex: { in: careIndexes },
      });
    }

    if (form.fciGroupType?.length) {
      whereInputs.push({
        fciGroup: { in: form.fciGroupType },
      });
    }

    if (form.hairType?.length) {
      whereInputs.push({
        hairType: { in: form.hairType },
      });
    }

    return { ...(whereInputs.length ? { AND: whereInputs } : {}) } as DogBreedWhereInput;
  }, []);

  return { breedsWhereBuilder };
};
